import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { fabric } from 'fabric';
import { ToastVariants } from '@dotcom/crown-ui';
import { getPhotoTextZoneSettings } from '../../../components/card-editor/utils';
import { setIsToasterOpen, useAppContext } from '../../../context/app-context';
import { CardText, FabricTextBox } from '../../../global-types';
import { useFeatureFlags } from '../../../hooks';
import { getFontName, CanvasDataTypes } from '../../../utils';
import { addPhotoTextZoneButton } from '../utils/add-photo-text-zone-button';
import { addPhotoTextZoneButtons } from '../utils/add-photo-text-zone-buttons';
import { tamFitsInZone } from '../utils/tam-fits-in-zone';

// Explicit types for hook functions
export type OnUserZoneError = (error: Error) => void;

export type CheckTextZoneBoundsProps = { textbox: fabric.Textbox; photoTextZone: fabric.Group };

export type LoadPhotoTextZoneProps = { zone: fabric.Group };

export type AddPhotoTextZoneProps = {
  canvas: React.RefObject<fabric.Canvas>;
  text: CardText;
  zoneIndex: number;
  zoneLabel: string;
  isShowingAltLabel?: boolean;
};

export const usePhotoTextZone = () => {
  const { appDispatch } = useAppContext();
  const { SAS_DYNAMIC_BUTTONS } = useFeatureFlags();
  const { t } = useTranslation();

  // Handles user zone errors by showing a toaster and throwing the error
  const onUserZoneError: OnUserZoneError = (error) => {
    setIsToasterOpen(appDispatch, {
      title: t('generalError.title'),
      children: t('generalError.message'),
      variant: ToastVariants.Error,
    });

    throw error;
  };

  // Checks if the textbox is within the photo text zone and shows a warning if not
  const checkTextZoneBounds = useCallback(
    ({ textbox, photoTextZone }: CheckTextZoneBoundsProps): void => {
      const isTextInsideZone = tamFitsInZone({ textbox, photoTextZone });
      if (isTextInsideZone) {
        photoTextZone.data.warned = false;
        return;
      }

      if (!photoTextZone.data.warned) {
        photoTextZone.data.warned = true;
        setIsToasterOpen(appDispatch, {
          title: t('typeMessage.warningTitle'),
          children: t('typeMessage.warningMessage'),
          variant: ToastVariants.Warning,
        });
      }
    },
    [appDispatch, t],
  );

  // Loads a photo text zone and attaches a change listener to its textbox
  const loadPhotoTextZone = useCallback(
    ({ zone }: LoadPhotoTextZoneProps): void => {
      const textbox = zone._objects.find((obj) => obj.type === 'textbox') as fabric.Textbox | undefined;
      if (textbox) {
        textbox.on('changed', () => {
          checkTextZoneBounds({ textbox, photoTextZone: zone });
        });
      }
    },
    [checkTextZoneBounds],
  );

  // Adds a new photo text zone to the canvas with appropriate settings and controls
  const addPhotoTextZone = useCallback(
    async ({ canvas, text, zoneIndex, zoneLabel }: AddPhotoTextZoneProps): Promise<void> => {
      const currentCanvas = canvas.current;
      if (!currentCanvas) {
        onUserZoneError(new Error('Canvas is not defined'));
        return;
      }
      // Get base zone settings
      const zoneSettings = getPhotoTextZoneSettings(text, zoneIndex);
      // Add Clip path so that text respects bounding box
      const clipPath = new fabric.Rect({
        ...zoneSettings,
        absolutePositioned: true,
      });
      // Add textbox to be populated with TAM.
      const originalText = new fabric.Textbox('', {
        textAlign: 'center',
        fontWeight: 'normal',
        fontSize: 32,
        top: Math.ceil(zoneSettings.top - zoneSettings.height / 2),
        left: Math.ceil(zoneSettings.left - zoneSettings.width / 2),
        fontFamily: getFontName(107),
        width: zoneSettings.width,
        hasBorders: false,
        lockMovementX: true,
        lockMovementY: true,
        lockRotation: true,
        controls: {},
        data: {
          linkedZoneName: zoneSettings.name,
          ID: text.ID,
        },
        CanResizeTextArea: zoneSettings.CanResizeTextArea,
        CanEditFontSize: zoneSettings.CanEditFontSize,
        CanEditFontColor: zoneSettings.CanEditFontColor,
        FontAutoSize: zoneSettings.FontAutoSize,
        name: `${CanvasDataTypes.PhotoZoneTextbox}-${zoneIndex}`,
        clipPath,
      } as CardText & FabricTextBox);

      // Get dotted photoTextZone border
      const photoTextZoneBorder = new fabric.Rect(zoneSettings);

      // Add all objects to photoTextZone group
      const photoTextZone = new fabric.Group([originalText, photoTextZoneBorder], zoneSettings);

      originalText.on('changed', function (this: fabric.Textbox) {
        checkTextZoneBounds({ textbox: this, photoTextZone });
      });

      currentCanvas.add(photoTextZone);

      if (SAS_DYNAMIC_BUTTONS) {
        photoTextZone.set('selectable', false);
        addPhotoTextZoneButtons({ canvas: currentCanvas, zone: photoTextZone, isZoneHybrid: text.IsHybrid });
      } else {
        addPhotoTextZoneButton({
          zone: photoTextZone as fabric.Group & FabricTextBox,
          currentCanvas,
          label: zoneLabel,
        });
      }
    },
    [
      SAS_DYNAMIC_BUTTONS,
      getPhotoTextZoneSettings,
      getFontName,
      CanvasDataTypes,
      addPhotoTextZoneButtons,
      addPhotoTextZoneButton,
      t,
      checkTextZoneBounds,
    ],
  );

  return { addPhotoTextZone, loadPhotoTextZone, checkTextZoneBounds };
};
